import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    DayPickerRangeController,
    FocusedInputShape,
    DayPickerSingleDateController,
} from 'react-dates'
import moment, { Moment } from 'moment'
import 'moment/locale/pl'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import { Wrapper } from './styles'
import CustomButton from './components/CustomButton'
import CustomMonth, { CustomMonthProps } from './components/CustomMonth'
import { ReadPresenter } from 'store/api-slice/hooks'
import Day from './components/Day'
import { useApp } from 'store/steps'
import { getDatesBetweenDates } from 'helpers/dates'
import { useAppSelector } from 'store/hooks'
import { useCart } from 'store/cart-slice/hooks'
import {
    sumTicketsBundlesDates,
    sumTicketsDates,
} from 'components/global/Summary/helpers'
import { LegendBadgeIconType } from 'components/global/CalendarModal/components/CalendarModalLegend/components/CalendarLegendItem/components/CalendarLegendBadge'

interface CalendarProps {
    mode: 'single' | 'range' | 'buses' | 'accommodation'
    handleDatesChosen: (dates: Moment[]) => void
    datesInfo: ReadPresenter | null
    isLimitedRange: boolean
    rangeLimit?: number
    isDisabled: boolean
    date?: Moment | null
    rangeDateStart?: Moment | null
    rangeDateEnd?: Moment | null
    testIdPrefix?: string
    hasDarkMode?: boolean
    hasAdditionalInfo?: boolean
    isCurrentDayDisabled?: boolean
}

interface DatesProps {
    startDate: Moment | null
    endDate: Moment | null
}
interface VisualDatesProps {
    displayedStartDate: Moment | null
    displayedEndDate: Moment | null
}

const Calendar = ({
    mode,
    handleDatesChosen,
    datesInfo,
    isLimitedRange,
    rangeLimit = 3,
    isDisabled,
    date,
    rangeDateStart,
    rangeDateEnd,
    testIdPrefix,
    hasDarkMode,
    hasAdditionalInfo,
    isCurrentDayDisabled = false,
}: CalendarProps) => {
    const { isCreatorAccommodation } = useApp()
    const { isStepEmpty } = useCart()

    const { tickets, ticketsBundles, accommodation } = useAppSelector(
        (state) => state.cart.items
    )

    const [dateDefiningMonth, setDateDefiningMonth] = useState(
        moment(datesInfo?.from)
    )
    const [focusedRange, setFocusedRange] =
        useState<FocusedInputShape>('startDate')
    const [singleDay, setSingleDay] = useState<Moment | null>(null)
    const [{ startDate, endDate }, setDates] = useState<DatesProps>({
        startDate: null,
        endDate: null,
    })
    const [{ displayedStartDate, displayedEndDate }, setDatesVisually] =
        useState<VisualDatesProps>({
            displayedStartDate: rangeDateStart || null,
            displayedEndDate: rangeDateEnd || null,
        })

    const { detail } = accommodation || {}
    const { dateFrom, dateTo } = detail || {}

    const hasAlreadyBookedDatesFromTickets =
        !isCreatorAccommodation ||
        (isStepEmpty('accommodation') && !isStepEmpty('tickets'))

    const bookedDates = useMemo(() => {
        if (hasAlreadyBookedDatesFromTickets) {
            return tickets.length
                ? sumTicketsDates(tickets)
                : sumTicketsBundlesDates(ticketsBundles)
        } else {
            // get booked dates from accommodation
            if (dateFrom && dateTo)
                return getDatesBetweenDates(dateFrom, dateTo)
        }

        return []
    }, [
        hasAlreadyBookedDatesFromTickets,
        tickets,
        ticketsBundles,
        dateFrom,
        dateTo,
    ])

    const setDatesStates = (arg: {
        startDate: moment.Moment | null
        endDate: moment.Moment | null
    }) => {
        const { startDate, endDate } = arg
        if (
            startDate?.isBetween(displayedStartDate, displayedEndDate) ||
            startDate?.isBefore(displayedStartDate) ||
            startDate?.isSameOrAfter(displayedEndDate)
        ) {
            setDates({
                startDate,
                endDate: null,
            })
            setDatesVisually({
                displayedStartDate: startDate,
                displayedEndDate: null,
            })
        } else {
            setDates(arg)
            setDatesVisually({
                displayedStartDate: startDate,
                displayedEndDate: endDate,
            })
        }
    }

    const isHighlighted = (day: Moment, isHighlightningEnabled: boolean) =>
        !!isHighlightningEnabled &&
        bookedDates.includes(day.format('YYYY-MM-DD'))

    const isBlocked = (day: Moment) => {
        if (datesInfo && datesInfo.excluded && datesInfo.to) {
            return (
                datesInfo.excluded.some((excludedDay: string) =>
                    moment(excludedDay).isSame(day, 'day')
                ) || moment(datesInfo.to).isBefore(day, 'day')
            )
        } else {
            return false
        }
    }

    const calculatedBadgeColors = (day: Moment) => {
        const events = datesInfo?.events || []
        const formattedDate = day.format('YYYY-MM-DD')
        const matchedEvent = events.find(({ dates }) =>
            dates?.includes(formattedDate)
        )

        return matchedEvent?.iconColor ? [matchedEvent?.iconColor] : []
    }

    const calculatedBadgeIcons = useCallback(
        (day: Moment): LegendBadgeIconType[] => {
            if (
                datesInfo &&
                (datesInfo.differentHours || datesInfo.differentPrices) &&
                mode !== 'buses' &&
                mode !== 'accommodation'
            ) {
                const hasDayDifferentPrices = !!datesInfo.differentPrices?.some(
                    (highlighted: string) =>
                        moment(highlighted).isSame(day, 'day')
                )
                const hasDayDifferentHours = !!datesInfo.differentHours?.some(
                    (highlighted: string) =>
                        moment(highlighted).isSame(day, 'day')
                )

                return [
                    ...(hasDayDifferentPrices
                        ? ['starYellow' as LegendBadgeIconType]
                        : []),
                    ...(hasDayDifferentHours
                        ? ['starBlue' as LegendBadgeIconType]
                        : []),
                ]
            }
            return []
        },
        [datesInfo, mode]
    )

    const sharedProps = {
        daySize: 45,
        hideKeyboardShortcutsPanel: true,
        enableOutsideDays: true,
        noBorder: true,
        weekDayFormat: 'ddd',
        isFocused: true,
        initialVisibleMonth: () => {
            if (date) {
                setDateDefiningMonth(date)
                return date
            } else if (rangeDateStart) {
                setDateDefiningMonth(rangeDateStart)
                return rangeDateStart
            } else {
                return moment(datesInfo?.from)
            }
        },
        onPrevMonthClick: setDateDefiningMonth,
        onNextMonthClick: setDateDefiningMonth,
        renderMonthElement: (props: CustomMonthProps) => (
            <CustomMonth
                hasDarkMode={hasDarkMode}
                hasAdditionalInfo={hasAdditionalInfo}
                {...props}
            />
        ),
        transitionDuration: 0,
        isDayBlocked: isBlocked,
    }

    const handleFocusChange = (focusedInput: FocusedInputShape | null) => {
        setFocusedRange(focusedInput ? 'endDate' : 'startDate')
    }

    useEffect(() => {
        if (mode === 'single' || mode === 'buses') {
            if (singleDay !== null) handleDatesChosen([singleDay])
        } else {
            if (startDate !== null && endDate !== null)
                handleDatesChosen([startDate, endDate])
        }
    }, [singleDay, startDate, endDate])

    return (
        <Wrapper
            hasDarkMode={hasDarkMode}
            hasAdditionalInfo={hasAdditionalInfo}
            hasDaysFromEarlierStepsMarked={!!bookedDates.length}
        >
            {mode === 'single' || mode === 'buses' ? (
                <DayPickerSingleDateController
                    date={singleDay || date || null}
                    onDateChange={(date) =>
                        setSingleDay(isDisabled ? singleDay : date)
                    }
                    focused={true}
                    onFocusChange={({ focused }) => null}
                    renderDayContents={(day) => (
                        <Day
                            isHighlighted={isHighlighted(day, mode === 'buses')}
                            dayToRender={day}
                            testId={`${
                                testIdPrefix ? `${testIdPrefix}-` : ''
                            }calendar-day-${day.format('DD-MM')}`}
                            colorBadgeColors={calculatedBadgeColors(day)}
                            iconBadgeIcons={calculatedBadgeIcons(day)}
                            hasDarkMode={hasDarkMode}
                        />
                    )}
                    isOutsideRange={(day) =>
                        day.isBefore(moment(datesInfo?.from)) ||
                        day.isAfter(moment(datesInfo?.to).add(1, 'day')) ||
                        isDisabled ||
                        (isCurrentDayDisabled && day.isSame(moment(), 'day'))
                    }
                    renderNavNextButton={({ onClick }) => (
                        <CustomButton
                            isDisabled={
                                dateDefiningMonth.format('MMYYYY') ===
                                    moment(datesInfo?.to).format('MMYYYY') ||
                                isDisabled
                            }
                            onClick={onClick}
                            direction="-next"
                            testId={`${
                                testIdPrefix ? `${testIdPrefix}-` : ''
                            }calendar-button-next-month`}
                            hasDarkMode={hasDarkMode}
                        />
                    )}
                    renderNavPrevButton={({ onClick }) => (
                        <CustomButton
                            isDisabled={
                                dateDefiningMonth.format('MMYYYY') ===
                                    moment(datesInfo?.from).format('MMYYYY') ||
                                isDisabled
                            }
                            onClick={onClick}
                            direction="-prev"
                            testId={`${
                                testIdPrefix ? `${testIdPrefix}-` : ''
                            }calendar-button-prev-month`}
                            hasDarkMode={hasDarkMode}
                        />
                    )}
                    firstDayOfWeek={1}
                    {...sharedProps}
                />
            ) : (
                <DayPickerRangeController
                    minimumNights={mode !== 'accommodation' ? 0 : 1}
                    focusedInput={focusedRange}
                    onFocusChange={handleFocusChange}
                    startDate={displayedStartDate}
                    endDate={displayedEndDate}
                    onDatesChange={(dates) => {
                        setDatesStates(
                            isDisabled
                                ? {
                                      startDate,
                                      endDate,
                                  }
                                : dates
                        )
                    }}
                    renderDayContents={(day) => (
                        <Day
                            isHighlighted={isHighlighted(
                                day,
                                mode === 'accommodation' ||
                                    isCreatorAccommodation
                            )}
                            dayToRender={day}
                            testId={`${
                                testIdPrefix ? `${testIdPrefix}-` : ''
                            }calendar-day-${day.format('DD-MM')}`}
                            colorBadgeColors={calculatedBadgeColors(day)}
                            iconBadgeIcons={calculatedBadgeIcons(day)}
                            hasDarkMode={hasDarkMode}
                        />
                    )}
                    isOutsideRange={(day) => {
                        if (startDate && isLimitedRange && !endDate) {
                            return (
                                day.isBefore(datesInfo?.from) ||
                                day.isAfter(
                                    moment(startDate).add(rangeLimit - 1, 'day')
                                ) ||
                                datesInfo?.excluded?.some(
                                    (date) =>
                                        startDate.isBefore(date) &&
                                        day.isSameOrAfter(date)
                                ) ||
                                false
                            )
                        } else {
                            return (
                                day.isBefore(moment(datesInfo?.from)) ||
                                day.isAfter(moment(datesInfo?.to).add(1, 'day'))
                            )
                        }
                    }}
                    firstDayOfWeek={1}
                    renderNavNextButton={({ onClick }) => (
                        <CustomButton
                            isDisabled={
                                dateDefiningMonth.format('MMYYYY') ===
                                    moment(datesInfo?.to).format('MMYYYY') ||
                                isDisabled
                            }
                            onClick={onClick}
                            direction="-next"
                            testId={`${
                                testIdPrefix ? `${testIdPrefix}-` : ''
                            }calendar-button-next-month`}
                            hasDarkMode={hasDarkMode}
                        />
                    )}
                    renderNavPrevButton={({ onClick }) => (
                        <CustomButton
                            isDisabled={
                                dateDefiningMonth.format('MMYYYY') ===
                                    moment(datesInfo?.from).format('MMYYYY') ||
                                isDisabled
                            }
                            onClick={onClick}
                            direction="-prev"
                            testId={`${
                                testIdPrefix ? `${testIdPrefix}-` : ''
                            }calendar-button-next-month`}
                            hasDarkMode={hasDarkMode}
                        />
                    )}
                    {...sharedProps}
                />
            )}
        </Wrapper>
    )
}

export default Calendar
